import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import {
  applyCatalogTotalFromFetch,
  applyCatalogTotalFromMutation,
} from "../../utils/catalogTotalRecords";


import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import "./manageDevices.scss";

const DEVICE_SORT_FIELDS = {
  name: "DeviceName",
  deviceId: "DeviceID",
  registrationDate: "RegistrationDate",
};

const DEVICE_SORT_LABELS = {
  [DEVICE_SORT_FIELDS.name]: "Name",
  [DEVICE_SORT_FIELDS.deviceId]: "Device ID",
  [DEVICE_SORT_FIELDS.registrationDate]: "Registration Date",
};

const ManageDevices = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "devices";
  const defaultVisibleColumns = {
    DeviceID: true,
    Name: true,
    Device_Code: false,
    OS: false,
    Model: false,
    App: false,
    AppVersion: false,
    DeviceStatus: true,
    RegistrationDate: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "DeviceID", label: "Device ID" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const devicesFetchSeqRef = useRef(0);
  const [statusConfirmDevice, setStatusConfirmDevice] = useState(null);

  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: DEVICE_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (
      needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)
    ) {
      const mergedColumns = mergeVisibleColumns(
        preferences.visibleColumns,
        defaultVisibleColumns
      );
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  const fetchDevices = useCallback(async () => {
    const fetchSeq = ++devicesFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_DEVICES_OF_COMPANY, {
        params: {
          CompID,
          Search: search?.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
        },
      });

      if (fetchSeq !== devicesFetchSeqRef.current) return;

      const devicesData = response.data.data || response.data;
      const list = Array.isArray(devicesData) ? devicesData : [];
      setDevices(list);

      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, list.length);
      }
    } catch (error) {
      if (fetchSeq !== devicesFetchSeqRef.current) return;
      showErrorToast("Failed to load devices.");
      setDevices([]);
    } finally {
      if (fetchSeq === devicesFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, search, sortBy, sortOrder]);

  const totalRecords = devices.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedDevices = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return devices.slice(start, start + size);
  }, [devices, pageNumber, pageSize]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(devicesFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchDevices(), [fetchDevices]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const hasFiltersApplied = () => search.trim() !== "";
  const isUnfilteredView = !hasFiltersApplied();

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    isUnfilteredView &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No devices match your search "${search.trim()}". Try different keywords or clear filters to see all devices.`;
    }
    return "There are no devices to show right now.";
  };

  const renderNoDataMessage = () => {
    if (loading || devices.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No devices found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  const handleDeleteDevice = async (device) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${device.deviceName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_COMPANY_DEVICE, {
        params: {
          CompID,
          DeviceID: device.deviceID,
        },
      });

      if (response.data.status) {
        setDevices((prevDevices) => {
          const next = prevDevices.filter(
            (delDevice) => delDevice.deviceID !== device.deviceID
          );
          if (!search.trim()) {
            applyCatalogTotalFromMutation(setCatalogTotalRecords, next.length);
          }
          return next;
        });
        showSuccessToast("Device deleted successfully.");
      } else {
        showErrorToast(response.data.message || "Deletion failed.");
      }
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong while deleting the device.");
      setLoading(false);
    }
  };

  const handleSelectItem = (deviceId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (pagedDevices[i]?.deviceID) {
            newSelected.add(pagedDevices[i].deviceID);
          }
        }
      } else {
        if (newSelected.has(deviceId)) {
          newSelected.delete(deviceId);
        } else {
          newSelected.add(deviceId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(pagedDevices.map((item) => item.deviceID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No devices selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected devices?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((deviceId) =>
          axiosInstance.delete(APIs.DELETE_COMPANY_DEVICE, {
            params: {
              CompID,
              DeviceID: deviceId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchDevices();
      showSuccessToast(`${selectedItems.size} devices deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected devices.");
      setLoading(false);
    }
  };

  const confirmDeviceStatusChange = async () => {
    if (!statusConfirmDevice) return;

    const { deviceID, deviceStatus, deviceName } = statusConfirmDevice;
    const isCurrentlyActive =
      (deviceStatus || "").toLowerCase() === "active";
    const newStatus = isCurrentlyActive ? "Inactive" : "Active";
    const prevDevices = devices;

    setDevices((prev) =>
      prev.map((item) =>
        item.deviceID === deviceID ? { ...item, deviceStatus: newStatus } : item
      )
    );
    setStatusConfirmDevice(null);

    try {
      const response = await axiosInstance.patch(APIs.PATCH_UPDATE_DEVICE_STATUS, {
        CompID,
        DeviceID: deviceID,
        Status: newStatus,
      });

      if (!response?.data?.status) {
        throw new Error(response?.data?.message || "Update failed");
      }
      showSuccessToast(
        `"${deviceName || "Device"}" is now ${newStatus === "Active" ? "Active" : "InActive"}.`
      );
    } catch (error) {
      setDevices(prevDevices);
      showErrorToast(
        error?.response?.data?.message || "Failed to update device status."
      );
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Devices</li>
          </ol>
          <h1 className="page-header mb-0">Manage Devices</h1>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && (
          <GridSectionLoader loading minHeight="400px" />
        )}

        {showEmptyCatalogState && (
          <div className="alert alert-info text-center mb-0">
            No devices registered for this company yet.
          </div>
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Devices"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by device name, ID, OS..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              {SHOW_BULK_ACTIONS && selectedItems.size > 0 && (
                <div className="mb-3">
                  <div
                    className="p-3 bg-light rounded d-flex align-items-center justify-content-between"
                    style={{ border: "1px solid #dee2e6" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} device
                        {selectedItems.size > 1 ? "s" : ""} selected
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                        title="Delete Selected Devices"
                      >
                        <i className="fa fa-trash me-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && pagedDevices.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table
                      className="table table-hover manage-devices-table"
                      style={{
                        "--modern-grid-sort-active": themeColor,
                      }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {SHOW_BULK_ACTIONS && (
                            <th className="pt-2 pb-2">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  pagedDevices.length > 0 &&
                                  selectedItems.size === pagedDevices.length
                                }
                              />
                            </th>
                          )}
                          {visibleColumns.Name && (
                            <SortableColumnHeader
                              sortField={DEVICE_SORT_FIELDS.name}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Name
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.DeviceID && (
                            <SortableColumnHeader
                              sortField={DEVICE_SORT_FIELDS.deviceId}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Device ID
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.Device_Code && (
                            <th className="pt-2 pb-2 text-center">Device Code</th>
                          )}
                          {visibleColumns.OS && (
                            <th className="pt-2 pb-2 text-center">OS</th>
                          )}
                          {visibleColumns.Model && (
                            <th className="pt-2 pb-2 text-center">Model</th>
                          )}
                          {visibleColumns.App && (
                            <th className="pt-2 pb-2 text-center">App</th>
                          )}
                          {visibleColumns.AppVersion && (
                            <th className="pt-2 pb-2 text-center">App Version</th>
                          )}
                          {visibleColumns.RegistrationDate && (
                            <SortableColumnHeader
                              sortField={DEVICE_SORT_FIELDS.registrationDate}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Registration Date
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.DeviceStatus && (
                            <th className="pt-2 pb-2 text-center">Status</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedDevices.map((device, index) => (
                          <tr key={device.deviceID}>
                            {SHOW_BULK_ACTIONS && (
                              <td className="align-middle">
                                <input
                                  type="checkbox"
                                  checked={selectedItems.has(device.deviceID)}
                                  onChange={(e) =>
                                    handleSelectItem(device.deviceID, index, e)
                                  }
                                  data-chkbox-shiftsel="type1"
                                />
                              </td>
                            )}
                            {visibleColumns.Name && (
                              <td className="align-middle text-center">
                                {device.deviceName}
                              </td>
                            )}
                            {visibleColumns.DeviceID && (
                              <td className="align-middle text-center">
                                {device.deviceID}
                              </td>
                            )}
                            {visibleColumns.Device_Code && (
                              <td className="align-middle text-center">
                                {device.device_Code}
                              </td>
                            )}
                            {visibleColumns.OS && (
                              <td className="align-middle text-center">
                                {device.os}
                              </td>
                            )}
                            {visibleColumns.Model && (
                              <td className="align-middle text-center">
                                {device.model}
                              </td>
                            )}
                            {visibleColumns.App && (
                              <td className="align-middle text-center">
                                {device.app}
                              </td>
                            )}
                            {visibleColumns.AppVersion && (
                              <td className="align-middle text-center">
                                {device.appVersion}
                              </td>
                            )}
                            {visibleColumns.RegistrationDate && (
                              <td className="align-middle text-center">
                                {new Date(device.registrationDate).toLocaleString()}
                              </td>
                            )}
                            {visibleColumns.DeviceStatus && (
                              <td
                                className="align-middle text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <StatusBadge
                                  isActive={
                                    (device.deviceStatus || "").toLowerCase() ===
                                    "active"
                                  }
                                  onClick={() => setStatusConfirmDevice(device)}
                                />
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <TableActionsMenu dropup menuStyle={{ zIndex: 9999 }}>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => handleDeleteDevice(device)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <CustomModal
        title="Change device status?"
        subtitle={
          statusConfirmDevice
            ? `Are you sure you want to set "${statusConfirmDevice.deviceName || "this device"}" to ${
                (statusConfirmDevice.deviceStatus || "").toLowerCase() === "active"
                  ? "InActive"
                  : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmDevice(null)}
        isOpen={!!statusConfirmDevice}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmDeviceStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManageDevices;
